import { clsx } from 'clsx';

export interface StatusChipProps {
  status: string;
  variant?: 'default' | 'outlined' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  color?: 'gray' | 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'amber';
  className?: string;
  children: React.ReactNode;
}

const colorVariants = {
  gray: {
    default: 'bg-muted text-muted-foreground border border-border',
    outlined: 'text-muted-foreground border border-border bg-background',
    filled: 'bg-muted-foreground text-background',
  },
  blue: {
    default: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20',
    outlined: 'text-blue-600 dark:text-blue-400 border border-blue-500/30 bg-background',
    filled: 'bg-blue-500 text-white',
  },
  green: {
    default: 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20',
    outlined: 'text-green-600 dark:text-green-400 border border-green-500/30 bg-background',
    filled: 'bg-green-500 text-white',
  },
  yellow: {
    default: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border border-yellow-500/20',
    outlined: 'text-yellow-700 dark:text-yellow-400 border border-yellow-500/30 bg-background', 
    filled: 'bg-yellow-500 text-white',
  },
  red: {
    default: 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20',
    outlined: 'text-red-600 dark:text-red-400 border border-red-500/30 bg-background',
    filled: 'bg-red-500 text-white',
  },
  purple: {
    default: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20',
    outlined: 'text-purple-600 dark:text-purple-400 border border-purple-500/30 bg-background',
    filled: 'bg-purple-500 text-white',
  },
  amber: {
    default: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
    outlined: 'text-amber-700 dark:text-amber-400 border border-amber-500/30 bg-background',
    filled: 'bg-amber-500 text-white',
  },
};

export function StatusChip({
  status,
  variant = 'default',
  size = 'sm',
  color = 'gray',
  className,
  children,
}: StatusChipProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-medium',
        colorVariants[color][variant],
        {
          'px-2 py-0.5 text-xs': size === 'sm',
          'px-2.5 py-1 text-sm': size === 'md',
          'px-3 py-1.5 text-base': size === 'lg',
        },
        className
      )}
      data-status={status}
    >
      {children}
    </span>
  );
}

// Pre-configured status chips for common use cases
export function ApplicationStatusChip({ status }: { status: string }) {
  const colorMap: Record<string, StatusChipProps['color']> = {
    applied: 'blue',
    phone_screen: 'purple',
    interview: 'amber',
    final_interview: 'yellow',
    offer: 'green',
    rejected: 'red',
    withdrawn: 'gray',
  };

  const labelMap: Record<string, string> = {
    applied: 'Applied',
    phone_screen: 'Phone Screen',
    interview: 'Interview',
    final_interview: 'Final Interview', 
    offer: 'Offer',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  };

  return (
    <StatusChip 
      status={status}
      color={colorMap[status] || 'gray'}
    >
      {labelMap[status] || status}
    </StatusChip>
  );
}

export function ResumeStatusChip({ 
  status, 
  type 
}: { 
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'retrying';
  type?: 'parse' | 'tag' | 'render';
}) {
  const colorMap: Record<typeof status, StatusChipProps['color']> = {
    pending: 'gray',
    processing: 'blue',
    completed: 'green',
    failed: 'red',
    retrying: 'yellow',
  };

  const labelMap: Record<typeof status, string> = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed', 
    retrying: 'Retrying',
  };

  return (
    <StatusChip
      status={status}
      color={colorMap[status]}
      size="sm"
    >
      {type && status === 'processing' ? `${type.charAt(0).toUpperCase() + type.slice(1)}ing...` : labelMap[status]}
    </StatusChip>
  );
}

export function SavedJobBucketChip({ bucket }: { bucket: 'to_apply' | 'applied' | 'archived' }) {
  const colorMap: Record<typeof bucket, StatusChipProps['color']> = {
    to_apply: 'blue',
    applied: 'green',
    archived: 'gray',
  };

  const labelMap: Record<typeof bucket, string> = {
    to_apply: 'To Apply',
    applied: 'Applied',
    archived: 'Archived',
  };

  return (
    <StatusChip
      status={bucket}
      color={colorMap[bucket]}
    >
      {labelMap[bucket]}
    </StatusChip>
  );
}